import { Injectable, EventEmitter } from '@angular/core';
import { SessionStorage, ApiQuoteResponse, ApiPackage, ApiMessage, ApiTraveler, ApiResponseContainer, IApiMessage } from '@bhtp-client-api/base';
import { FieldRules, ConstraintRules } from '@bhtp-client-api/eligibility';
import { ModalService, ModalOptions, RulesService, FieldConfigs } from '@bhtp-client-web/component';
import { PackageAgreement } from '@bhtp-client-api/purchase';

import { Observable, Subscription, Subject } from 'rxjs';
import { NavigationStepComponent } from './navigation-step.component';
import { NavigationConfig } from './navigation-config.model';
import { StepConfig } from './step-config.model';
import { StepState } from './step-state.model';
import { QuotingService } from './quoting.service';
import { ApiQuoteTripResponse } from '@bhtp-client-api/purchase';
import {
    PurchaseData, EmailQuote, TramsService, TramsCustomQuote,
    PaymentFormData, CoverageContainer, EmailGroup, ReceiptData,
    AmbassadorInformationSessionStorage,
    Agent, FeatureService
} from './../shared';
import { WriteValueOverride } from './models';
import { SaveQuoteService } from '../quotes/save-quote.service';
import { EmailQuoteService } from '../email-quote/email-quote.service';
import { GatewayErrorResponse } from '../shared/models/agents-gateway/errors/gateway-error-reesponse.model';
import { GWCreateInsuredEventResponse } from '../shared/models/agents-gateway/save-quote/gw-create-insured-event-response.model';
import { GWUpdateInsuredEventResponse } from '../shared/models/agents-gateway/save-quote/gw-update-insured-event-response.model';
import { EmailQuoteData } from '../email-quote';

interface IBhtpWindow extends Window {
    global_disabledSaveSendAgencyList: any;
}
declare var window: IBhtpWindow;

@Injectable()
export class PurchaseNavigationService {
    public steps: Array<StepConfig> = new Array<StepConfig>();
    public stepStates: Array<StepState> = new Array<StepState>();
    public currentStep: number = 0;
    public stepChanged: EventEmitter<string> = new EventEmitter<string>(false);

    public currentQuote: ApiQuoteResponse;

    public dataChanged: EventEmitter<PurchaseData> = new EventEmitter<PurchaseData>();
    public purchaseCompleted: EventEmitter<string> = new EventEmitter<string>();

    private config: NavigationConfig;
    private activeComponent: NavigationStepComponent;
    private fieldConfigs: FieldConfigs;
    private constraints: ConstraintRules;
    private dataChangedSubscription: Subscription;
    private handlePurchaseError: Subject<ApiMessage[]> = new Subject();
    private showButton: boolean;

    constructor(private sessionStorage: SessionStorage, private rulesService: RulesService, private quoteService: QuotingService,
            private modalService: ModalService, private ambassadorSessionStorageService: AmbassadorInformationSessionStorage,
            private saveQuoteService: SaveQuoteService, private emailQuoteService: EmailQuoteService, private featureService: FeatureService
    ) {
        this.dataChangedSubscription = this.quoteService.purchaseDataUpdated.subscribe(updatedData => { this.storePurchaseData(updatedData); });
    }

    public updateRules(rules: FieldRules, constraints: ConstraintRules): void {
        this.fieldConfigs = this.rulesService.buildFieldConfigs(rules, RulesService.PURCHASE_MODE);
        this.setPackageConstraints();
        this.constraints = constraints;
    }

    public setConfig(config: NavigationConfig, rules: FieldRules, constraints: ConstraintRules): void {
        this.fieldConfigs = this.rulesService.buildFieldConfigs(rules, RulesService.PURCHASE_MODE);
        this.constraints = constraints;
        this.config = config;
        this.setPackageConstraints();
        this.steps = new Array<StepConfig>();
        this.stepStates = new Array<StepState>();

        if (this.config.hasTravelerStep) {
            this.createStep('Travelers', 'travelers', true);
        }

        if (this.config.hasCoveragesStep) {
            this.createStep('Add Coverages', 'coverages', false);
        }

        if (this.config.hasFlightStep) {
            this.createStep('Flights', 'flights', false);
        }

        if (this.config.hasTripStep) {
            this.createStep('Finish Quote', 'finish', false);
        }

        this.createStep('Purchase', 'purchase', false);

        // if data was from another session, clear the current data
        let purchaseData: PurchaseData = this.getSessionPurchaseData();
        if (purchaseData.sessionId.toString() !== this.config.sessionId.toString()) {
            this.storePurchaseData(null);
        }

        // load up current quote so data is available for situations like refreshes
        let quote: ApiQuoteResponse = this.quoteService.getCurrentQuote();
        if (quote) {
            this.currentQuote = quote;
        }

        // activate the first step, or the passed in step
        if (this.config.initialStep) {
            let index: number = this.getIndexForStep(this.config.initialStep);
            this.setActiveStep(index, false);
        }
        else {
            this.setActiveStep(0, false);
        }
    }

    public initializeCurrentComponent(component: NavigationStepComponent): void {
        // Ideally this should be in ngOnInit, but agent portal does everything in the constructor.
        this.redirectIncompleteDeepLinks();

        // track the current component and set the purchase data on it
        this.activeComponent = component;
        this.activeComponent.currentPurchaseData = this.getSessionPurchaseData();
        this.activeComponent.fieldConfigs = this.fieldConfigs;
        this.activeComponent.constraints = this.constraints;
        this.activeComponent.currentPackage = this.config.package;
        this.activeComponent.packageAgreements = this.config.agreementsForAgent;

        // make step build up its form form validation
        this.activeComponent.buildForm();
    }

    // specific step was clicked on. see if the visited/valid
    // state of other steps allow this, and then attempt to continue
    public stepRequested(step: StepConfig): void {
        if (step) {
            // if step is a previous step, navigate directly to it
            let index: number = this.getIndexForStep(step.step);
            if (index < this.currentStep) {
                if (this.currentStep > 0) {
                    // Save the data to the model, do not override
                    this.writeCurrentFormToPurchaseData(WriteValueOverride.SaveState);
                }

                this.setActiveStep(index, true);
            }
            else if (index > this.currentStep) {
                // check steps between the current and selected one for validity
                let allStepsVistedAndValid = true;
                for (let i = this.currentStep + 1; i < index; i++) {
                    let stepState: StepState = this.stepStates[i];
                    if (!stepState.visited || !stepState.valid) {
                        allStepsVistedAndValid = false;
                        break;
                    }
                }

                // if all subsequent steps are valid, perform continuation check on
                // current step
                if (allStepsVistedAndValid) {
                    this.activeComponent.canContinue().subscribe(canContinue => {
                        if (canContinue) {
                            this.onContinueToTab(index);
                        }
                    });
                }
            }
        }
    }

    public swapPackage(newRatingId: string, newPackage: ApiPackage): void {
        // update the current package
        this.config.package = newPackage;
        this.config.ratingId = newRatingId;
        this.activeComponent.currentPackage = this.config.package;

        let data: PurchaseData = this.writeCurrentFormToPurchaseData();
        this.dataChanged.emit(data);

        // quote all coverages on product swap because coverages change for new product
        this.quoteService.getQuote(data, true, null, true, false).subscribe(result => {
            if (result) {
                this.currentQuote = result;
            }
        }, err => {
            this.modalService.showMessageModal('Error', 'The quote request failed.');
        });
    }

    public nextRequested(): void {
        this.activeComponent.submitted = true;
        this.activeComponent.tryingToContinue = true;

        this.activeComponent.canContinue().subscribe(canContinue => {
            if (canContinue) {
                this.onContinue();
            }
        });
    }

    public purchaseQuoteSkipQueue(paymentFormData: PaymentFormData): void {
        this.purchaseQuote(paymentFormData, true);
    }

    public purchaseQuote(paymentFormData: PaymentFormData, skipQueue: boolean = false): void {
        this.modalService.showLoadingModal('Purchasing...');
        let currentPurchaseData: PurchaseData = this.writeCurrentFormToPurchaseData();
        this.quoteService.purchaseQuote(currentPurchaseData, paymentFormData.billing, skipQueue)
            .subscribe((result: ApiResponseContainer<ApiQuoteResponse>) => {
                if (result && result.response) {
                    this.quoteService.storeCurrentQuote(result.response);
                    this.modalService.hideLoadingModal();

                    let receipt: ReceiptData = ReceiptData.createFromPurchaseData(currentPurchaseData, result.response, paymentFormData.billing, this.activeComponent.currentPackage);
                    this.sessionStorage.storeValue(`receipt-${result.response.policyNumber}`, JSON.stringify(receipt));
                    this.ambassadorSessionStorageService.clearSessionData();
                    this.purchaseCompleted.emit(result.response.policyNumber.toString());
                }
            }, (err: ApiMessage[] | any) => {
                console.log('Purchase failed: ' + err);
                this.handlePurchaseQuoteError(err);
                this.handlePurchaseError.next(err);
            }, () => {
                this.modalService.hideLoadingModal();
            });
    }

    public getHandlePurchaseError(): Subject<ApiMessage[]> {
        return this.handlePurchaseError;
    }

    // give option to continue, ex: this.activeComponent.canContinue()
    // prevented navigation, but step would like to advance
    public onContinue(): void {
        if (this.currentStep < this.steps.length - 1) {
            this.onContinueToTab(this.currentStep + 1);
        }
    }

    public previousRequested(): void {
        if (this.currentStep > 0) {
            // Save the data to the model, do not override
            this.writeCurrentFormToPurchaseData(WriteValueOverride.SaveState);
            this.setActiveStep(this.currentStep - 1, true);
        }
    }

    public showNextButton(): boolean {
        return this.currentStep < (this.steps.length - 1) && !this.steps[this.currentStep].hideNextButton;
    }

    public showPreviousButton(): boolean {
        return this.currentStep > 0 && this.steps && this.steps[this.currentStep] && !this.steps[this.currentStep].hidePreviousButton;
    }

    public showSaveQuoteButton(): boolean {
        return this.showEmailSaveQuoteButton();
    }

    public showEmailQuoteButton(): boolean {
        let showButton: boolean = false;
        showButton = this.showEmailSaveQuoteButton();

        if (this.currentStep > 0) {
            try {
                if (this.currentQuote &&
                    this.currentQuote.package &&
                    this.currentQuote.package.availablePlatform &&
                    !this.currentQuote.package.availablePlatform.includes("Web")) {
                        showButton = false;
                    }
            }
            catch (err) {
                console.warn("Could not determine showEmail button status");
                showButton = false;
            }
        }

        return showButton;
    }

    // only display email button if not first step and an email is provided for primary traveler
    private showEmailSaveQuoteButton(): boolean {
        let showButton: boolean = false;

        if (this.currentStep > 0) {
            let purchaseData: PurchaseData = this.getStoredValue('purchaseData');
            if (purchaseData &&
                purchaseData.primaryTraveler &&
                purchaseData.primaryTraveler.emailGroup &&
                !purchaseData.primaryTraveler.emailGroup.noEmail &&
                window.global_disabledSaveSendAgencyList.indexOf(this.currentQuote.agencyCode) === -1) {
                    showButton = true;
                }
        }

        return showButton;
    }

    public showPriceInformation(): boolean {
        return this.steps && this.steps[this.currentStep] && this.steps[this.currentStep].hidePriceInformation !== true;
    }

    public currentActiveStep(): string {
        return this.steps && this.steps[this.currentStep] ? this.steps[this.currentStep].step : null;
    }

    public resetNavigation(): void {
        this.steps = new Array<StepConfig>();
        this.stepStates = new Array<StepState>();
        this.storePurchaseData(null);
        this.storeConfiguration(null);
    }

    public getQuoteByQuoteNumber(quoteNumber: string, sessionId: number): Observable<any> {
        if (quoteNumber && !this.purchaseDataExistsForSession(sessionId)) {
            const getQuote: Observable<any> = this.quoteService.getQuoteByQuoteNumber(quoteNumber);
            getQuote.subscribe(quote => {
                this.initializeQuoteData(quote.response, sessionId, true);
            }, err => {
                this.handleErrorMessages('Load Existing Quote Error', err);
            }, null);

            return getQuote;
        } else {
            return Observable.of({});
        }
    }

    public async emailQuote(emailQuote: EmailQuoteData, agent: Agent): Promise<void> {
        try {
            const data: PurchaseData = await this.saveQuote(true);
            this.modalService.showLoadingModal('Emailing Quote...');
            const quote = this.quoteService.getCurrentQuote();
            const quoteCopy = JSON.parse(JSON.stringify(quote));
            await this.emailQuoteService.convertAndEmailQuote(data, quoteCopy, emailQuote, agent);
            this.modalService.hideLoadingModal();
            this.modalService.showMessageModal('Quote Sent', `Quote number is ${data.quoteNumber}`);
        } catch(err) {
            this.handleEmailQuoteError(err);
        } finally {
            this.modalService.hideLoadingModal();
        }
    }

    public async saveQuote(emailQuote: boolean = false): Promise<PurchaseData> {
        try {

            this.modalService.showLoadingModal('Saving Quote...');
            const purchaseData: PurchaseData = this.writeCurrentFormToPurchaseData();
            const quote = this.quoteService.getCurrentQuote();
            const quoteCopy = JSON.parse(JSON.stringify(quote));
            let response: GWUpdateInsuredEventResponse | GWCreateInsuredEventResponse;

            if (!purchaseData.quoteNumber) {
                response = await this.saveQuoteService.saveEvent(purchaseData, [quoteCopy]);
            } else {
                response = await this.saveQuoteService.updateEvent(purchaseData, [quoteCopy]);
            }

            const [actualQuoteId, quoteNumber, eventId] = this.extractIdsFromResponse(response);

            quote.quoteId = actualQuoteId;
            purchaseData.quoteNumber = quoteNumber;
            purchaseData.eventId = eventId;

            this.quoteService.storeCurrentQuote(quote);
            this.storePurchaseData(purchaseData);
            quote.package = this.currentQuote.package
            this.currentQuote = quote;

            this.modalService.hideLoadingModal();

            if (!emailQuote) {
                this.modalService.showMessageModal('Quote Saved', `Quote number is ${quoteNumber}`);
            }

            return purchaseData;
        } catch (err) {
            this.handleSaveQuoteError(err);
        } finally {
            this.modalService.hideLoadingModal();
        }
    }

    extractIdsFromResponse(response: GWUpdateInsuredEventResponse | GWCreateInsuredEventResponse): [string, string, number] {
        let actualQuoteId: string = null;
        let quoteNumber: string = null;
        let eventId: number = null;

        if (response && response.event) {
            eventId = response.event.id;

            if (response.event.quotes && response.event.quotes[0] && response.event.quotes[0].id) {
                actualQuoteId = response.event.quotes[0].id.toString();
            }
        }

        const useEventIdAsQuoteNumber = response.source === 'events';
        if (useEventIdAsQuoteNumber && eventId) {
            quoteNumber = eventId.toString();
        } else {
            quoteNumber = actualQuoteId;
        }

        return [actualQuoteId, quoteNumber, eventId];
    }

    public quoteCurrentPurchaseData(emitResult: boolean, quoteCoverages: boolean, flightCount: number): Observable<ApiQuoteResponse> {
        let data: PurchaseData = this.writeCurrentFormToPurchaseData(WriteValueOverride.Override);
        const getQuote: Observable<ApiQuoteResponse> = this.quoteService.getQuote(data, false, flightCount, emitResult, quoteCoverages);
        getQuote.subscribe(
            (result) => {
                if (result) {
                    this.quoteService.storeCurrentQuote(result);
                    this.currentQuote = result;
                }
            },
            (err) => {
                this.modalService.showMessageModal('Error', 'Failed to requote.');
            });

        return getQuote;
    }

    /*
     * Gets session from storage, calls the component to write the new values,
     * and saves the result into storage.
     * NOTE - no valid check is done, so the component should do this check before writing values
     */
    public writeCurrentFormToPurchaseData(override: WriteValueOverride = WriteValueOverride.Default): PurchaseData {
        let data: PurchaseData = null;

        if (this.activeComponent) {
            data = this.activeComponent.currentPurchaseData;
        }
        else {
            data = this.getSessionPurchaseData();
        }

        this.activeComponent.writeValues(data, override);
        this.storePurchaseData(data);
        return this.applyAmbassadorOverrides(data);
    }

    /*
     * Gets session from storage. If it doesnt exist, new it up and save it.
     * Assigns the rating id from the config
     */
    public getSessionPurchaseData(): PurchaseData {
        let purchaseData: PurchaseData = this.getStoredValue('purchaseData');
        if (!purchaseData) {
            purchaseData = new PurchaseData();
            this.storePurchaseData(purchaseData);
        }

        return this.applyAmbassadorOverrides(purchaseData);
    }

    public setPrimaryTravelerToPurchaseData(customer: ApiTraveler, emitDataChanged: boolean = false): void {
        if (customer) {
            let data: PurchaseData = this.getSessionPurchaseData();
            data.primaryTraveler.firstName = customer.firstName;
            data.primaryTraveler.lastName = customer.lastName;
            data.primaryTraveler.gender = (customer.gender ? customer.gender.toLocaleLowerCase() : '');
            data.primaryTraveler.birthDate = customer.birthDate;
            data.primaryTraveler.phoneNumber = customer.phoneNumber;

            // if a state was selected on the quick quote page, use that value instead of what is
            // defined in the customer address
            let currentState: string = null;
            if (data.primaryTraveler.address && data.primaryTraveler.address.stateOrProvince) {
                currentState = data.primaryTraveler.address.stateOrProvince;
            }

            data.primaryTraveler.address = customer.address;
            if (currentState && data.primaryTraveler.address) {
                data.primaryTraveler.address.stateOrProvince = currentState;
            }

            data.primaryTraveler.emailGroup = new EmailGroup();
            data.primaryTraveler.emailGroup.email = customer.email || null;

            // if an email exists, we want the checkbox to be empty, otherwise check it
            data.primaryTraveler.emailGroup.noEmail = data.primaryTraveler.emailGroup.email ? false : true;

            this.storePurchaseData(data);

            if (emitDataChanged) {
                this.dataChanged.emit(data);
            }
        }
    }

    private applyAmbassadorOverrides(purchaseData: PurchaseData): PurchaseData {

        // We are going to override the agent code
        purchaseData.agentCode = this.ambassadorSessionStorageService.getAgentCode() ? this.ambassadorSessionStorageService.getAgentCode() : purchaseData.agentCode;
        // We are going to override the agency code
        purchaseData.agencyCode = this.ambassadorSessionStorageService.getAgencyCode() ? this.ambassadorSessionStorageService.getAgencyCode() : purchaseData.agencyCode;
        return purchaseData;
    }

    private onContinueToTab(index: number): void {
        this.activeComponent.onContinue().subscribe(result => {
            this.stepStates[this.currentStep].valid = true;

            // have the component write its values to the data
            // and then overwrite the currently stored values
            let data: PurchaseData = this.writeCurrentFormToPurchaseData();
            this.dataChanged.emit(data);
            if (this.activeComponent.quoteBeforeContinue()) {
                this.modalService.showLoadingModal('Quoting...');
                this.quoteService.getQuote(data, true, null, true, !this.config.loadedQuote).subscribe(result => {
                    this.modalService.hideLoadingModal();
                    if (result) {
                        this.currentQuote = result;
                        this.config.loadedQuote = false;
                    }

                    this.setActiveStep(index, true);
                }, err => {
                    this.modalService.hideLoadingModal();
                    this.handleErrorMessages('Quoting Error', err);
                });
            }
            else {
                this.setActiveStep(index, true);
            }
        });
    }

    private quoteUpdateModal(data: PurchaseData): void {
        if (data.updateQuoteMessage) {
            this.modalService.showMessageModal('Quote Update', data.updateQuoteMessage);
            data.updateQuoteMessage = null;
        }
    }

    private getIndexForStep(step: string): number {
        let index: number = this.steps.findIndex(r => r.step === step);

        // if no matching step found, default to first step
        if (!index || index < 0) {
            index = 0;
        }

        return index;
    }

    // advances to next step in path. optionally emits an event
    // so router can update url
    private setActiveStep(index: number, emitEvent: boolean): void {
        // deactivate current step
        if (this.steps.length > this.currentStep && this.currentStep >= 0) {
            this.steps[this.currentStep].isActive = false;
        }

        // change to the requested step
        this.currentStep = index;
        let newStep: StepConfig = this.steps[this.currentStep];
        newStep.isActive = true;
        this.stepStates[this.currentStep].visited = true;

        if (emitEvent) {
            this.stepChanged.emit(newStep.step);
        }
    }

    private createStep(title: string, step: string, hidePriceInformation: boolean): void {
        let newStep: StepConfig = new StepConfig(title, step);
        newStep.hidePriceInformation = hidePriceInformation;
        this.steps.push(newStep);

        let newState: StepState = new StepState();
        newState.step = step;
        this.stepStates.push(newState);
    }

    private initializeQuoteData(quote: ApiQuoteTripResponse, sessionId: number, isLoadedQuote: boolean = false): void {
        let data: PurchaseData = this.quoteService.mapQuoteTripResponseToPurchaseData(quote, sessionId);

        // Here we are going to clear out any agent/agency codes that may be in session because we have a new policy/quote being
        // loaded with its own agent/agency codes
        this.ambassadorSessionStorageService.clearSessionData();
        this.ambassadorSessionStorageService.setAgentCode(data.agentCode);

        // Clear out customer id from config to prevent customer swapping
        // Customer data in config could be left over from previous session
        if (this.config) {
            this.config.customerId = null;
        }

        this.storePurchaseData(data, isLoadedQuote);
    }

    private storePurchaseData(data: PurchaseData, isLoadedQuote: boolean = false): void {
        let serializedData: string = null;
        if (data && this.config) {
            // If a quote is being loaded from an email or saved quote we want to make sure that
            // we set the session to that of the loaded quote. Otherwise the data will be reset
            if (isLoadedQuote) {
                this.config.sessionId = data.sessionId;
            }
            // This is stale data and we need to reset the session Id
            else {
                data.sessionId = this.config.sessionId;
            }

            if (this.config.customerId && !data.customerId) {
                data.customerId = this.config.customerId;
            }

            data.ratingId = this.config.ratingId;

            data = this.applyAmbassadorOverrides(data);

            this.quoteUpdateModal(data);

            serializedData = JSON.stringify(data);
        }
        // This is a save quote in which the config has yet to be loaded,  it will load in the next step.
        else if (data && isLoadedQuote) {
            serializedData = JSON.stringify(data);
        }

        if (serializedData != null) {
            this.sessionStorage.storeValue('purchaseData', serializedData);
        }
        else {
            this.sessionStorage.removeValue('purchaseData');
        }

        if (this.activeComponent) {
            this.activeComponent.currentPurchaseData = data;
        }

        // Updates the PolicyHolder session information.
        let policyHolder = JSON.parse(this.sessionStorage.getValue(`FinishQuote-PolicyHolder-${data.sessionId}`));
        if (policyHolder) {
            this.sessionStorage.storeValue(`FinishQuote-PolicyHolder-${data.sessionId}`, JSON.stringify(data.primaryTraveler));
        }

        // Updates the AdditionalTravelers session information.
        let additionalTravelers = JSON.parse(this.sessionStorage.getValue(`FinishQuote-AdditionalTravelers-${data.sessionId}`));
        if (additionalTravelers) {
            this.sessionStorage.storeValue(`FinishQuote-AdditionalTravelers-${data.sessionId}`, JSON.stringify(data.additionalTravelers));
        }
    }

    private getConfiguration(): NavigationConfig {
        let configuration: NavigationConfig = this.getStoredValue('config');
        if (!configuration) {
            configuration = new NavigationConfig();
            this.storeConfiguration(configuration);
        }

        return configuration;
    }

    private storeConfiguration(configuration: NavigationConfig): void {
        let serializedData: string = configuration ? JSON.stringify(configuration) : null;
        this.sessionStorage.storeValue('config', serializedData);
    }

    private setMaxTripLength(): void {
        if (this.hasPackageConfiguration()) {
            this.fieldConfigs.policy.tripDates.maxTripLength = this.config.package.configuration.maximumTripLength;
        }
    }

    private setMinTripLength(): void {
        if (this.hasPackageConfiguration()) {
            this.fieldConfigs.policy.tripDates.minTripLength = this.config.package.configuration.minimumTripLength;
        }
    }

    private setMinAge(): void {
        if (this.hasPackageConfiguration()) {
            this.fieldConfigs.policyHolder.minAge = this.config.package.configuration.minimumAge;
            this.fieldConfigs.additionalTraveler.minAge = this.config.package.configuration.minimumAge;
        }
    }

    private setMaxAge(): void {
        if (this.hasPackageConfiguration()) {
            this.fieldConfigs.policyHolder.maxAge = this.config.package.configuration.maximumAge;
            this.fieldConfigs.additionalTraveler.maxAge = this.config.package.configuration.maximumAge;
        }
    }

    private hasPackageConfiguration() {
        return this.config && this.config.package && this.config.package.configuration;
    }

    private getStoredValue(key: string): any {
        try {
            let data: string = this.sessionStorage.getValue(key);
            if (data) {
                return JSON.parse(data);
            }
        }
        catch (err) {
            return null;
        }
    }

    private purchaseDataExistsForSession(sessionId: number): boolean {
        let purchaseData: PurchaseData = this.getStoredValue('purchaseData');
        return (purchaseData !== undefined && purchaseData !== null && purchaseData.sessionId === sessionId);
    }

    private handleEmailQuoteError(error: GatewayErrorResponse): void {
        let options: ModalOptions = this.modalService.getApiMessageModalOptions(
            `Email Quote Failed: ${error.name}`,
             this.convertGatewayMessagesToApiMessages(error));
        this.modalService.showApiMessageModal(options);
    }

    private handleSaveQuoteError(error: GatewayErrorResponse): void {
        let options: ModalOptions = this.modalService.getApiMessageModalOptions(
            `Save Quote Failed: ${error.name}`,
             this.convertGatewayMessagesToApiMessages(error));
        this.modalService.showApiMessageModal(options);
    }

    private handlePurchaseQuoteError(error: ApiMessage[]): void {
        let options: ModalOptions = this.modalService.getApiMessageModalOptions('Purchase Quote Failed', error);
        this.modalService.showApiMessageModal(options);

        // hides loading modal - continue on observable doesn't get called on error
        this.modalService.hideLoadingModal();
    }

    private handleErrorMessages(title: string, error: ApiMessage[]): void {
        let options: ModalOptions = this.modalService.getApiMessageModalOptions(title, error);
        this.modalService.showApiMessageModal(options);
    }

    private setPackageConstraints(): void {
        this.setMaxTripLength();
        this.setMinTripLength();
        this.setMinAge();
        this.setMaxAge();
    }

    private convertGatewayMessagesToApiMessages(error: any): IApiMessage[] {
        if (error && error.validationMessages) {
            return error.validationMessages.map((m) => {
                return {
                    text: m,
                    code: '',
                    type: null,
                    severity: null,
                    acknowledgements: []
                };
            });
        } else {
            return [{
                text: error.message,
                code: '',
                type: null,
                severity: null,
                acknowledgements: []
            }];
        }
    }

    private redirectIncompleteDeepLinks() {
        if (this.currentStep === 0) {
            const currentPurchaseData: PurchaseData = JSON.parse(this.sessionStorage.getValue('purchaseData'));
            if (!currentPurchaseData || !currentPurchaseData.primaryTraveler || !currentPurchaseData.primaryTraveler.address || !currentPurchaseData.primaryTraveler.address.postalCode) {
                // Would have used router, or location services but there was injection issues with the angular 1 + 2 mixed solution
                window.location.href = '/quickquote';
                return;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/purchase-navigation.service.ts